// cleanup_class_to_function_SECURITY_QUESTIONS
// cleanup_remove_external_libraries_SECURITY_QUESTIONS
// cleanup_convert_to_typescript_file_SECURITY_QUESTIONS

import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { template } from 'lodash';
import Form from 'react-validation/build/form';
import Button from 'react-validation/build/button';
import classnames from 'classnames';

import Alert from '@glu/alerts-react';
import { withStyles } from '@glu/theming';

import { ALERTVARIATIONS } from 'constants.js';
import { getSecurityQuestions, validateQAChallenge } from 'api/securityQuestions/service';
import { unsubscribeComponent } from 'components/utils/util';
import Input from 'components/input/Input';
import required from 'utils/validations/required';

import styles from './SecurityChallenge.styles';

export class SecurityChallengeComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      sending: false,
      loading: !this.props.questions,
      errorMessage: '',
      questions: this.props.questions
    };

    this.mounted = false;

    this.subscriptions = {
      getQuestions: null
    };
  }

  componentDidMount() {
    const { questions } = this.state;
    this.mounted = true;
    if (!questions || !questions.length) {
      this.getSecurityQuestions();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    unsubscribeComponent(this.subscriptions);
  }

  getSecurityQuestions = () => {
    const { customerCode, userCode } = this.props.user;
    const { locale } = this.props;

    if (!userCode || !customerCode) {
      this.props.onError(locale['logon.forgotten.system.warning']);
    } else {
      this.subscriptions.getQuestions = getSecurityQuestions(customerCode, userCode)
        .then(this.setQuestions)
        .catch(this.props.onError);
    }
  };

  setQuestions = ({ data, success }) => {
    const { locale } = this.props;

    if (!success) {
      const { code, userMessage } = data?.[0];

      this.props.onError(locale[code] || userMessage);
    } else if (this.mounted) {
      this.setState({ questions: data, loading: false });
    }
  };

  clearError = () => {
    this.setState({ errorMessage: '' });
  };

  validateQuestions = (event) => {
    event.preventDefault();

    if (this.state.sending) {
      return false;
    }

    const { user } = this.props;
    const { questions } = this.state;
    const answers = questions.reduce(
      (body, { answer, id }) => ({
        ...body,
        [`answer${id}`]: answer
      }),
      user
    );

    this.setState({ sending: true });

    validateQAChallenge(answers).then(this.handleResponse).catch(this.handleErrors);
    return false;
  };

  handleErrors = () => {
    this.setState({
      sending: false,
      errorMessage: this.props.locale['logon.forgotten.system.error']
    });
  };

  handleResponse = ({ data, success }) => {
    const { locale } = this.props;

    if (!data.lockedOut && success) {
      const message = template(locale['password.success'])({
        email: data.emailId || '',
        phoneNumber: data.phoneNumber || ''
      });
      this.setState({ sending: false });
      this.props.onSuccess(message);
    } else {
      const { code, userMessage } = data?.[0];
      const errorMessage = locale[code] || userMessage || data.message;
      this.setState({ sending: false, errorMessage });
    }
  };

  handleInputChange = ({ target: { name: inputName, value } }) => {
    const { questions: prevQuestions } = this.state;

    const questions = prevQuestions.map((question) => ({
      ...question,
      answer: question.key === inputName ? value : question.answer
    }));

    this.setState({ questions });
  };

  render() {
    const { classes, locale } = this.props;
    const { errorMessage, loading, questions, sending } = this.state;

    if (loading) {
      return null;
    }

    if (!questions || !questions.length) {
      return null;
    }

    return (
      <Form onSubmit={this.validateQuestions} className={`${classes.form}`}>
        <fieldset>
          <legend className="sr-only">{locale.logInTitle}</legend>
          {errorMessage && (
            <Alert
              dataQa="securityChallengeAlert"
              variant={ALERTVARIATIONS.ERROR}
              onHide={this.clearError}
              message={errorMessage}
            />
          )}

          {questions.map(({ key: name, question }) => (
            <article className="form-group" key={name}>
              <label htmlFor={`question-${name}`}>
                {question}
                <br />
                {locale['common.answer']}
              </label>

              <Input
                className="form-control"
                disabled={sending}
                id={`question-${name}`}
                data-qa={name}
                locale={{ required: locale['security.required'] }}
                name={name}
                type="text"
                onChange={this.handleInputChange}
                required
                validations={[required]}
              />
            </article>
          ))}
        </fieldset>
        <article className="form-group">
          <Button
            type="submit"
            data-qa="submit-answers"
            className={classnames('btn btn-primary', { disabled: sending })}
          >
            {locale['payment.submit']}
          </Button>
        </article>
      </Form>
    );
  }
}

SecurityChallengeComponent.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  questions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  locale: PropTypes.objectOf(PropTypes.string),
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  user: PropTypes.shape({
    userCode: PropTypes.string,
    customerCode: PropTypes.string
  })
};

SecurityChallengeComponent.defaultProps = {
  questions: [],
  locale: {},
  onError() {},
  onSuccess() {},
  user: {}
};

export default withStyles(styles)(SecurityChallengeComponent);
